import {
  DateRange,
  MedicalInformation,
  NotesOutlined,
  Pets,
} from "@mui/icons-material";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import {
  alpha,
  Box,
  Card,
  CardContent,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import { BiPencil } from "react-icons/bi";
import { CiCalendar } from "react-icons/ci";
import { useParams } from "react-router-dom";
import { Fragment } from "react/jsx-runtime";
import GoBack from "../../../../components/buttons/GoBack";
import PageContentWrapper from "../../../../components/wrappers/PageContentWrapper";
import {
  GET_SINGLE_THERAPY,
  GET_THERAPY_DAYS,
} from "../../../../enums/ApiPaths";
import {
  SINGLE_THERAPY_KEY,
  THERAPY_DAYS_FETCH,
} from "../../../../enums/QueryKeys";
import {
  SAPS_HOME_PAGE,
  SINGLE_BOX_VIEW_NO_ID,
} from "../../../../enums/UrlPaths";
import { useFetch } from "../../../../hooks/Fetching";
import { getBoxNavigationId, useIsMobile } from "../../../../utils/Functions";
import { DialogText } from "../../components/core/DialogCore";
import NotFound from "../../components/display/NotFound";
import DeleteTherapy from "../../components/forms/DeleteTherapy";
import EditTherapyGeneralInfo from "../../components/forms/EditTherapyGeneralInfo";
import HorizontalStack from "../../components/wrappers/HorizontalStack";
import { TabPanel } from "./SinglePetView";
import EditTherapyDay from "./components/therapyView/EditTherapyDay";
import TherapySchedule from "./components/therapyView/TherapySchedule";
import FinishTherapy from "../../components/forms/FinishTherapy";
import AlertBar from "../../components/display/AlertBar";

export default function SingleTherapyView() {
  const { id } = useParams();
  const url = GET_SINGLE_THERAPY + id;
  const { t } = useTranslation();

  const { data, isLoading, error } = useFetch(url, null, [
    SINGLE_THERAPY_KEY,
    id,
  ]);

  if (isLoading) {
    return (
      <PageContentWrapper noSideMargin>
        <Skeleton
          variant="rounded"
          height={400}
          sx={{ maxWidth: 1200, mx: "auto" }}
        />
      </PageContentWrapper>
    );
  } else if (error) {
    return (
      <PageContentWrapper noSideMargin>
        <NotFound title={t("ErrorFetchingTherapyDetails")} />
      </PageContentWrapper>
    );
  }

  return (
    <PageContentWrapper noSideMargin>
      <Stack spacing={2}>
        <TherapyHeader therapy={data} />
        <TherapyTabs therapy={data} />
      </Stack>
    </PageContentWrapper>
  );
}

function TherapyHeader({ therapy = null }) {
  const isArchived = therapy.archived;
  const boxId = getBoxNavigationId();
  const url = isArchived
    ? -1
    : boxId
      ? SINGLE_BOX_VIEW_NO_ID + boxId
      : SAPS_HOME_PAGE;
  const { t } = useTranslation();
  return (
    <Fragment>
      <HorizontalStack>
        <GoBack url={url} />
        {isArchived ? null : (
          <HorizontalStack spacing={2}>
            <DeleteTherapy />
            <EditTherapyGeneralInfo therapy={therapy} />
            {therapy?.dischargeLetter == null && (
              <FinishTherapy therapy={therapy} />
            )}
          </HorizontalStack>
        )}
      </HorizontalStack>

      {isArchived ? (
        <AlertBar severity="warning" message={t("ArchivedTherapyUserInfo")} />
      ) : null}

      <Card
        sx={{
          overflow: "hidden",
          boxShadow: 3,
          borderRadius: 2,
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <Stack spacing={3}>
            {/* Header Section */}
            <Box>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                flexWrap="wrap"
                gap={2}
              >
                <Box sx={{ mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1,
                    }}
                  >
                    <LocalHospitalIcon color="primary" />
                    <Typography
                      variant="h5"
                      fontWeight="700"
                      sx={{ wordBreak: "break-word" }}
                    >
                      {therapy?.title}
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Pets sx={{ fontSize: 20 }} color="primary" />
                    <Typography
                      variant="h6"
                      color="text.secondary"
                      fontWeight="500"
                    >
                      {therapy?.petName}
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Box>

            <Divider />

            {therapy?.startDate && (
              <Paper
                elevation={0}
                sx={{ p: 3, bgcolor: "background.default", borderRadius: 2 }}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight="600"
                  gutterBottom
                  sx={{ display: "flex", alignItems: "center", gap: 1 }}
                >
                  <DateRange color="primary" /> {t("TreatmentPeriod")}
                </Typography>
                <Stack spacing={1} mt={1}>
                  {therapy?.startDate && (
                    <InfoItem
                      icon={<CiCalendar />}
                      label="StartDate"
                      value={therapy.startDate}
                    />
                  )}
                  {therapy?.endDate && (
                    <InfoItem
                      icon={<CiCalendar />}
                      label="EndDate"
                      value={therapy.endDate}
                    />
                  )}
                </Stack>
              </Paper>
            )}

            {/* Dialog sections as expandable details */}
            <Box sx={{ mt: 2 }}>
              <DialogText
                title={t("Description")}
                text={therapy?.description}
                dialogTitle={t("Description")}
                key="therapy_description"
                buttonVariant="outlined"
                buttonSize="small"
              />
              <Box sx={{ mt: 1 }}>
                <DialogText
                  title={t("TreatmentApplication")}
                  text={therapy?.administration}
                  dialogTitle={t("WayOfAdministration")}
                  key="administration_details"
                  buttonVariant="outlined"
                  buttonSize="small"
                />
              </Box>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Fragment>
  );
}

function TherapyTabs({ therapy }) {
  const url = GET_THERAPY_DAYS;
  const { t } = useTranslation();
  const { id } = useParams();
  const { data, isLoading, error } = useFetch(url, id, [
    THERAPY_DAYS_FETCH,
    id,
  ]);
  const isArchived = therapy.archived;
  const [activeTab, setActiveTab] = React.useState(0);
  const handleTabChange = (event, newValue) => setActiveTab(newValue);
  const theme = useTheme();
  const isMobile = useIsMobile();
  const [scheduleKey, setScheduleKey] = React.useState(0);
  React.useEffect(() => {
    setScheduleKey((prev) => prev + 1);
  }, [therapy]);

  if (isLoading) {
    return <Skeleton variant="rectangular" height={500} />;
  } else if (error) {
    return (
      <Typography color={"error"}>
        {t("ErrorLoadingTherapySchedules")}
      </Typography>
    );
  }

  const datesArray = data?.map((value) => {
    return value.date;
  });

  return (
    <Fragment>
      <Card
        sx={{
          mb: 3,
          borderRadius: 3,
          width: "100%",
          maxWidth: "100%",
          overflow: "hidden",
        }}
      >
        <CardContent sx={{ p: 0 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              maxWidth: "100%",
              "& .MuiTabs-flexContainer": {
                flexWrap: "nowrap",
              },
              "& .MuiTab-root": {
                fontWeight: 600,
                fontSize: { xs: "0.75rem", sm: "1rem" },
                py: { xs: 1, sm: 2 },
                textTransform: "none",
                minWidth: { xs: 80, sm: 140 },
                flexDirection: isMobile ? "column" : "row",
                "& svg": {
                  fontSize: { xs: 18, sm: 22 },
                  mb: isMobile ? 0.5 : 0,
                },
                "&.Mui-selected": {
                  color: theme.palette.primary.main,
                },
              },
            }}
          >
            <Tab
              iconPosition="start"
              icon={<MedicalInformation />}
              label={t("TherapySchedule")}
            />
            {!isArchived ? (
              <Tab
                iconPosition="start"
                icon={<BiPencil />}
                label={t("EditTherapyDay")}
              />
            ) : null}
            {therapy?.dischargeLetter && (
              <Tab
                iconPosition="start"
                icon={<NotesOutlined />}
                label={t("DischargeLetter")}
              />
            )}
          </Tabs>
        </CardContent>
      </Card>

      {/* Panels */}
      <Box sx={{ overflowX: "hidden", width: "100%" }}>
        <TabPanel value={activeTab} index={0}>
          <TherapySchedule
            days={data}
            key={scheduleKey}
            isArchived={isArchived}
          />
        </TabPanel>
        {isArchived ? null : (
          <TabPanel value={activeTab} index={1}>
            <EditTherapyDay
              key={"edit_single_therapy_day"}
              dates={datesArray}
              days={data}
            />
          </TabPanel>
        )}
        <TabPanel value={activeTab} index={isArchived ? 1 : 2}>
          <FinishTherapy
            key={"finish_single_therapy"}
            therapy={therapy}
            inline
            isArchived={isArchived}
          />
        </TabPanel>
      </Box>
    </Fragment>
  );
}

function InfoItem({ icon, label, value }) {
  const { t } = useTranslation();
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      <Box sx={{ color: "primary.main" }}>{icon}</Box>
      <Box>
        <Typography variant="caption" color="text.secondary">
          {t(label)}
        </Typography>
        <Typography variant="body2" fontWeight="500">
          {value}
        </Typography>
      </Box>
    </Stack>
  );
}
